// 🔧 sponsors.js
// ✅ Purpose: Public-facing sponsor directory for a specific event
// ✅ Features:
// - Uses useEventContext() to load the current event
// - Loads sponsor levels from /events/{eventId}/settings/voting
// - Loads sponsors from /events/{eventId}/sponsors
// - Groups sponsors by sponsor level
// - Sponsors without a valid level are grouped into "Event Sponsors"
// - Shows each sponsor in a white card so logos display cleanly
// - Shows sponsor note / “Become a Sponsor” content when configured
// - Hides empty sponsor note if not configured
// - Public-facing page available to event attendees
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import "react-quill/dist/quill.snow.css";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import { getEntryPath } from "../../utils/entry_urls";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import { sanitizeHtml } from "../../utils/sanitize_html";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const Sponsors = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [sponsors, setSponsors] = useState([]);
  const [sponsorLevels, setSponsorLevels] = useState([]);
  const [sponsorsNote, setSponsorsNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (ready && eventId) trackEvent({ action: GA_EVENTS.SPONSOR_LIST_VIEWED, parameters: { event_slug: eventslug, event_id: eventId, source_page: window.location.pathname } });
  }, [ready, eventId, eventslug]);

  useEffect(() => {
    const loadSponsorPageData = async () => {
      if (!eventId) return;

      setLoading(true);

      try {
        logger.log("🌟 Loading public sponsors page", {
          eventId,
        });

        const settingsSnap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const settingsData = settingsSnap.exists()
          ? settingsSnap.data()
          : {};

        setSponsorLevels(settingsData.sponsorLevels || []);
        setSponsorsNote(settingsData.sponsorsNote || "");

        const sponsorsSnap = await getDocs(
        collection(db, "events", eventId, "sponsors")
        );

        const sponsorList = sponsorsSnap.docs.map((sponsorDoc) => ({
          id: sponsorDoc.id,
          ...sponsorDoc.data(),
        }));

        // ✅ Public sponsor visibility filter
        // ✅ Purpose:
        // - Only show sponsors that are approved or paid
        // - Prevents pending/unpaid sponsors from appearing publicly
        // - Keeps old sponsors visible if they do not have the new payment fields yet
        const visibleSponsors = sponsorList.filter((sponsor) => {
          const hasModernApprovalFields =
            sponsor.paymentStatus ||
            sponsor.approvalStatus ||
            sponsor.isApproved !== undefined;

          if (!hasModernApprovalFields) {
            return true;
          }

          return (
            sponsor.isApproved === true ||
            sponsor.approvalStatus === "approved" ||
            sponsor.paymentStatus === "paid" ||
            sponsor.paymentStatus === "waived" ||
            sponsor.paymentStatus === "not_required"
          );
        });

        setSponsors(visibleSponsors);

        const entriesSnap = await getDocs(
        collection(db, "events", eventId, "cars")
        );

        setEntries(
        entriesSnap.docs.map((entryDoc) => ({
            id: entryDoc.id,
            ...entryDoc.data(),
        }))
        );

        logger.log("✅ Public sponsors loaded", {
          eventId,
          count: sponsorList.length,
        });
      } catch (err) {
        logger.error("❌ Error loading public sponsors page:", {
          value: err,
        });

        setSponsors([]);
        setSponsorLevels([]);
        setSponsorsNote("");
      } finally {
        setLoading(false);
      }
    };

    loadSponsorPageData();
  }, [eventId]);

  // =====================================================
  // 🎗 Sponsors Add-on Page Gate
  // =====================================================
  // ✅ Purpose:
  // - Prevent direct URL access to sponsor pages when the Sponsors add-on is inactive
  // - Keeps public, sponsor-contact, and manager sponsor tools hidden
  // - Friendly message instead of exposing sponsor functionality
  // =====================================================
  const sponsorsEnabled =
    hasPurchasedAddOn(eventDoc, "sponsors") ||
    hasPurchasedAddOn(eventDoc, "sponsor");

  if (!sponsorsEnabled) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="alert alert-info text-center mb-0">
            Sponsors are not active for this event.
          </div>
        </div>
      </section>
    );
  }

  // =====================================================
  // 🔎 Dynamic SEO
  // Purpose:
  // - Gives the public sponsors page event-specific SEO metadata
  // - Uses event logo when available
  // - Keeps private/test events out of search unless publicSeo is enabled
  // =====================================================

  const breadcrumbs = [
    { name: "Home", path: "/" },
    {
      name: eventDoc?.name || "Event",
      path: `/${eventslug}/event`,
    },
    {
      name: "Sponsors",
      path: `/${eventslug}/sponsors`,
    },
  ];

  const approvedSponsorsSeo = buildEventPageSeo({
    eventDoc,
    pageKey: "sponsors",

    fallbackTitle: `${eventDoc?.name || "Event"} Sponsors | Let's Go Vote`,

    fallbackDescription: `Meet the sponsors supporting ${
      eventDoc?.name || "this event"
    }.`,
  });

  const sponsorSeo = {
    ...approvedSponsorsSeo,

    path: `/${eventslug}/sponsors`,

    breadcrumbs,
  };

  const sponsorLevelNames = sponsorLevels.map((level) =>
  typeof level === "string" ? level : level.name
  );

  const sponsorsByLevel = [
  ...sponsorLevelNames
      .map((levelName) => ({
      levelName,
      sponsors: sponsors.filter((sponsor) => sponsor.level === levelName),
      }))
      .filter((group) => group.sponsors.length > 0),

  {
      levelName: "Event Sponsors",
      sponsors: sponsors.filter(
      (sponsor) =>
          !sponsor.level ||
          !sponsorLevelNames.includes(sponsor.level)
      ),
  },
  ].filter((group) => group.sponsors.length > 0);

  const getSponsorAwardWinnerEntry = (sponsor) => {
  const winnerReg = String(sponsor.sponsorAwardWinnerReg || "").trim();

  if (!winnerReg) return null;

  return (
      entries.find(
      (entry) => String(entry.regcnt || "").trim() === winnerReg
      ) || null
  );
};
    
  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading Sponsors..." />;
  }

  return (
    <>
      <SeoHead {...sponsorSeo} />
      <section id="top-content" className="container-fluid py-5">
      <div className="container">

        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">🌟 Event Sponsors</h1>

            <p className="lead text-muted mb-0">
              {eventDoc?.name
                ? `Thank you to the sponsors supporting ${eventDoc.name}.`
                : "Thank you to the sponsors supporting this event."}
            </p>
          </div>
        </div>

        {sponsorsNote && (
          <div className="white-section bg-white p-4 rounded shadow-sm mb-4">
            <h4 className="fw-bold mb-3">🤝 Become a Sponsor</h4>

            <div
              className="ql-editor p-0"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(sponsorsNote) }}
            />
          </div>
        )}

        <div className="mt-3">
          <Link
            to={`/${eventslug}/become_sponsor`}
            className="btn btn-success"
          >
            🎗 Become a Sponsor
          </Link>
        </div>

        {sponsors.length === 0 ? (
        <div className="white-section bg-white p-4 rounded shadow-sm">
            <div className="alert alert-info mb-0">
            No sponsors have been added for this event yet.
            </div>
        </div>
        ) : (
        <div className="row">
            {(sponsorsByLevel.length > 0
            ? sponsorsByLevel
            : [
                {
                    levelName: "Event Sponsors",
                    sponsors,
                },
                ]
            ).map((group, index) => (
            <div key={index} className="col-12 mb-4">
                <div className="card shadow-sm border-0 h-100 bg-white">
                <div className="card-header bg-white border-0 pt-4 px-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                    <div>
                        <h4 className="fw-bold mb-1 text-dark">
                        🎗 {group.levelName}
                        </h4>

                        <p className="text-muted mb-0">
                        Thank you to our {group.levelName.toLowerCase()} supporters.
                        </p>
                    </div>

                    <span className="badge bg-dark mt-2 mt-md-0">
                        {group.sponsors.length} Sponsor
                        {group.sponsors.length === 1 ? "" : "s"}
                    </span>
                    </div>
                </div>

                <div className="card-body px-4 pb-4">
                    <div className="row">
                    {group.sponsors.map((sponsor) => {
                        const winnerEntry = getSponsorAwardWinnerEntry(sponsor);

                        return (
                        <div key={sponsor.id} className="col-12 col-md-6 mb-4">
                            <div className="bg-white border rounded shadow-sm p-3 h-100">
                            <Link
                                to={`/${eventslug}/sponsors/${sponsor.sponsorSlug || sponsor.id}`}
                                className="text-decoration-none"
                            >
                                <div
                                className="d-flex align-items-center justify-content-center bg-white border rounded mb-3"
                                style={{ minHeight: "130px" }}
                                >
                                {sponsor.logoUrl ? (
                                    <img
                                    src={sponsor.logoUrl}
                                    alt={sponsor.name || "Sponsor logo"}
                                    className="img-fluid"
                                    style={{
                                        maxHeight: "110px",
                                        objectFit: "contain",
                                    }}
                                    />
                                ) : (
                                    <div className="text-muted small">
                                    No Logo
                                    </div>
                                )}
                                </div>

                                <h5 className="fw-bold text-dark text-center mb-3">
                                {sponsor.name || "Unnamed Sponsor"}
                                </h5>
                            </Link>

                            {(sponsor.sponsorAwardTitle || sponsor.sponsorAwardWinnerReg) && (
                                <div className="alert alert-light border mb-3">
                                <div className="fw-bold text-dark">
                                    🏆 {sponsor.sponsorAwardTitle || "Sponsor Award"}
                                </div>

                                {winnerEntry ? (
                                    <div className="mt-2">
                                    {winnerEntry.imgUrl && (
                                        <div className="text-center mb-3">
                                        <img
                                            src={winnerEntry.imgUrl}
                                            alt={`Entry #${winnerEntry.regcnt}`}
                                            className="img-fluid rounded border shadow-sm"
                                            style={{
                                            maxHeight: "220px",
                                            objectFit: "contain",
                                            backgroundColor: "#fff",
                                            }}
                                        />
                                        </div>
                                    )}

                                    <div>
                                      <strong>
                                        Winner: #{winnerEntry.regcnt}
                                      </strong>
                                      {" — "}

                                      <Link
                                        to={getEntryPath(eventslug, winnerEntry)}
                                        className="fw-bold text-decoration-none"
                                      >
                                        {winnerEntry.description ||
                                          `${winnerEntry.year || ""} ${winnerEntry.model || ""}`.trim() ||
                                          "Entry details unavailable"}
                                      </Link>
                                    </div>

                                    {(
                                        winnerEntry.ownerName ||
                                        winnerEntry.ownerFirst ||
                                        winnerEntry.ownerLast ||
                                        winnerEntry.name
                                    ) && (
                                        <div className="small text-muted mt-1">
                                        👤 Owner: {
                                            winnerEntry.ownerName ||
                                            `${winnerEntry.ownerFirst || ""} ${winnerEntry.ownerLast || ""}`.trim() ||
                                            winnerEntry.name
                                        }
                                        </div>
                                    )}
                                    </div>
                                ) : sponsor.sponsorAwardWinnerReg ? (
                                    <div className="small text-muted mt-2">
                                    Winner registration #{sponsor.sponsorAwardWinnerReg} has been entered,
                                    but the entry could not be matched.
                                    </div>
                                ) : (
                                    <div className="small text-muted mt-2">
                                    Winner not selected yet.
                                    </div>
                                )}
                                </div>
                            )}

                            {sponsor.description && (
                                <div
                                className="ql-editor small p-0 text-muted"
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(sponsor.description) }}
                                />
                            )}
                            </div>
                        </div>
                        );
                    })}
                    </div>
                </div>
                </div>
            </div>
            ))}
        </div>
        )}

      </div>
    </section>
    </>
  );
};

export default Sponsors;
